import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { FaxQueries, FaxV1 } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { useFeatureAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useNav } from '../../nav/providers/nav-provider';
import { createNavItem, createSubNavItems } from '../nav-item-utils';
import { featureDefinition as faxFeatureDefinition } from './feature-definitions';

const FAX_ROOT = '/fax';

export const useFaxNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds } = useAppScopeStore();
  const { hasAccess: hasFaxAccess } = useFeatureAccess('fax-2-0', Permission.FAX_INBOX_MANAGE);

  const currentParent = useNav((state) => state.currentParent);
  const selectedParent = useNav((state) => state.selectedParent);
  const isFaxNavActive = currentParent === FAX_ROOT || selectedParent === FAX_ROOT;

  const { data: hasUnreadFaxesData } = FaxV1.Queries.useHasUnreadFaxesQuery({
    request: { locationIds: selectedLocationIds },
    options: { enabled: hasFaxAccess && selectedLocationIds.length > 0 },
  });

  const { data: faxUnreadCount } = FaxQueries.useCountFaxes(selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0 && isFaxNavActive,
  });

  const { data: faxDraftUnreadCount } = FaxDraftQueries.useGetDraftCount(selectedLocationIds[0], selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0 && !!selectedLocationIds[0] && isFaxNavActive,
  });
  const faxItems = faxFeatureDefinition.items;

  const subNavItems = useMemo(() => {
    const items = [
      {
        ...faxItems['/inbox'],
        relativePath: '/inbox',
        label: t('Fax Inbox'),
        count: faxUnreadCount?.faxCount,
        trackingId: faxItems['/inbox'].trackingId,
      },
      {
        ...faxItems['/drafts'],
        relativePath: '/drafts',
        label: t('Drafts'),
        count: faxDraftUnreadCount?.count,
        isPassiveCount: true,
        trackingId: faxItems['/drafts'].trackingId,
      },
      {
        ...faxItems['/archived'],
        relativePath: '/archived',
        label: t('Archived'),
        count: faxUnreadCount?.archivedFaxCount,
        isPassiveCount: true,
        trackingId: faxItems['/archived'].trackingId,
      },
      {
        ...faxItems['/contacts'],
        relativePath: '/contacts',
        label: t('Fax Contacts'),
        trackingId: faxItems['/contacts'].trackingId,
      },
      {
        ...faxItems['/blocked'],
        relativePath: '/blocked',
        label: t('Blocked'),
        count: faxUnreadCount?.blockedFaxCount,
        isPassiveCount: true,
        trackingId: faxItems['/blocked'].trackingId,
      },
      {
        ...faxItems['/drafts/new'],
        relativePath: '/drafts/new',
        label: t('Send New Fax'),
        trackingId: faxItems['/drafts/new'].trackingId,
      },
      {
        ...faxItems['/drafts/new-from-template'],
        relativePath: '/drafts/new-from-template',
        label: t('Send From Template'),
        trackingId: faxItems['/drafts/new-from-template'].trackingId,
      },
    ];

    return createSubNavItems(items);
  }, [faxDraftUnreadCount, faxItems, faxUnreadCount, t]);

  return useMemo(
    () =>
      createNavItem('fax', {
        iconName: 'fax',
        label: t('Fax'),
        root: '/fax',
        show: hasFaxAccess,
        hasCount: !!hasUnreadFaxesData?.hasUnread,
        subNavItems,
        settings: { path: '/fax', label: '' },
      }),
    [t, hasUnreadFaxesData?.hasUnread, hasFaxAccess, subNavItems]
  );
};
